/**
 * OnboardingPage - Integrated with Real API
 * Handles all onboarding scenarios with real backend integration
 */
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle } from 'lucide-react';
import OnboardingWizard from '@/components/onboarding/OnboardingWizard';
import { useOnboardingContext } from '@/providers/OnboardingProvider';
import { useAuth } from '@/providers/AuthProvider';
import { convertAPIScenarioToUI } from '@/hooks/useOnboarding';
import { OnboardingScenario, OnboardingState, FOProfileData, UserProfileData } from '@/types/onboarding';
import { User } from '@/types';
import { profileService } from '@/services/api/profileService';
import { API_CONFIG } from '@/config/api';
import { authService } from '@/services/authService';

export default function OnboardingPageIntegrated() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, scenario: apiScenario, isLoading, completeOnboarding } = useOnboardingContext();
  const { user } = useAuth();
  
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [manager, setManager] = useState<User | null>(null);
  const [loadingManager, setLoadingManager] = useState(false);
  const [existingProfileData, setExistingProfileData] = useState<Partial<UserProfileData> | undefined>(undefined);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [directReportsList, setDirectReportsList] = useState<any[]>([]);

  // Convert API scenario to UI format
  const uiScenario: OnboardingScenario = apiScenario 
    ? convertAPIScenarioToUI(apiScenario)
    : 'first-time-user';

  // Fetch manager details if user has a managerId
  useEffect(() => {
    const fetchManager = async () => {
      if (!user?.managerId) {
        setManager(null);
        return;
      }

      setLoadingManager(true);
      try {
        const baseURL = API_CONFIG.baseURL || 'http://localhost:8000';
        
        // Check if using test persona mode (managerId starts with "TEST")
        const isTestPersona = typeof user.managerId === 'string' && user.managerId.startsWith('TEST');
        
        let managerData: any = null;
        
        if (isTestPersona) {
          // Test persona mode: fetch from test-users endpoint
          const token = authService.getToken();
          const headers: Record<string, string> = {};
          if (token) headers['Authorization'] = `Bearer ${token}`;
          
          const response = await fetch(`${baseURL}/integration/test-users/${user.managerId}`, { headers });
          if (response.ok) {
            managerData = await response.json();
            setManager({
              id: managerData.azure_object_id || managerData.id,
              email: managerData.email,
              firstName: managerData.first_name,
              lastName: managerData.last_name,
              displayName: managerData.full_name,
              employeeId: managerData.azure_object_id || managerData.personnel_number,
              campus: managerData.campus,
              department: managerData.department,
              jobTitle: managerData.job_title,
              managerId: undefined,
              isActive: true,
              roles: managerData.roles || [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
          }
        } else {
          // Real user mode: fetch from identity service users endpoint
          const token = localStorage.getItem('access_token');
          const headers: Record<string, string> = {
            'Content-Type': 'application/json'
          };
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(`${baseURL}/identity/users/${user.managerId}`, { headers });
          if (response.ok) {
            managerData = await response.json();
            setManager({
              id: managerData.id,
              email: managerData.email,
              firstName: managerData.first_name,
              lastName: managerData.last_name,
              displayName: managerData.display_name,
              employeeId: managerData.employee_id,
              campus: managerData.campus,
              department: managerData.department,
              jobTitle: managerData.job_title,
              managerId: managerData.manager_id,
              isActive: managerData.is_active,
              roles: managerData.roles || [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            });
          } else {
            // Handle all non-OK responses gracefully (404, 500, 403, etc.)
            // Manager info is optional for onboarding - continue without it
            if (import.meta.env.DEV) console.log(`Could not fetch manager (ID: ${user.managerId}), status: ${response.status}`);
            setManager(null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch manager details:', error);
        setManager(null);
      } finally {
        setLoadingManager(false);
      }
    };

    fetchManager();
  }, [user?.managerId]);

  // Fetch direct reports if user is a manager
  useEffect(() => {
    const fetchDirectReports = async () => {
      if (!user?.employeeId) return;
      
      // Check if user has manager role
      const hasManagerRole = user.roles?.includes('manager') || user.roles?.includes('hr_admin') || user.roles?.includes('admin');
      if (!hasManagerRole) {
        setDirectReportsList([]);
        return;
      }

      try {
        // Fetch direct reports from identity service
        const baseURL = API_CONFIG.baseURL || '';
        const headers: Record<string, string> = {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          'Content-Type': 'application/json'
        };
        
        // Add test persona headers if in dev mode
        const devPersonaStr = localStorage.getItem('dev_selected_persona');
        if (devPersonaStr) {
          try {
            const devPersona = JSON.parse(devPersonaStr);
            headers['X-Test-User-Id'] = devPersona.personnelNumber || 'TEST001';
            headers['X-Test-User-Roles'] = (devPersona.roles || ['employee']).join(',');
          } catch (e) {
            console.warn('Failed to parse dev persona for direct reports fetch');
          }
        }
        
        const response = await fetch(`${baseURL}/identity/users/${user.employeeId}/direct-reports`, {
          headers
        });
        
        if (response.ok) {
          const reports = await response.json();
          setDirectReportsList(reports.map((r: any) => ({
            employeeId: r.employee_id,
            fullName: r.display_name,
            jobTitle: r.job_title,
            email: r.email
          })));
        } else {
          setDirectReportsList([]);
        }
      } catch (error) {
        console.error('Failed to fetch direct reports:', error);
        setDirectReportsList([]);
      }
    };

    fetchDirectReports();
  }, [user?.employeeId, user?.roles]);

  // Fetch existing profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      setLoadingProfile(true);
      try {
        const profile = await profileService.getCompleteProfile();
        
        // Convert API format to UI format
        setExistingProfileData({
          qualifications: profile.qualifications.map(q => ({
            id: `qual-${q.id}`,
            type: q.qualification_type as any,
            institution: q.institution,
            country: q.country || 'south_africa',
            
            // Academic details
            qualificationName: q.qualification_name,
            cesmCategory: q.cesm_category as any,
            fieldOfStudy: q.field_of_study,
            
            // NQF
            nqfLevel: q.nqf_level as any,
            
            // Completion status
            yearCompleted: q.year_completed,
            inProgress: q.in_progress,
            expectedCompletionYear: q.expected_completion_year,
            
            // International
            internationalEquivalency: q.international_equivalency as any,
            saqaEvaluated: q.saqa_evaluated,
            saqaReferenceNumber: q.saqa_reference_number,
            
            // Professional cert
            professionalBody: q.professional_body,
            membershipNumber: q.membership_number,
            membershipStatus: q.membership_status,
            
            // Research component
            thesis: q.thesis,
            thesisTitle: q.thesis_title,
            
            // Verification
            verificationDocument: q.verification_document_url
          })),
          careerHistory: profile.career_history.map(c => ({
            id: `career-${c.id}`,
            organization: c.organization,
            organizationType: c.organization_type,
            position: c.position,
            startDate: c.start_date,
            endDate: c.end_date,
            directReports: c.direct_reports,
            extendedTeamSize: c.extended_team_size,
            budgetResponsibility: c.budget_responsibility,
            budgetCurrency: c.budget_currency,
            keyAchievements: c.key_achievements,
            skillsDeveloped: c.skills_developed
          })),
          professionalContext: profile.professional_context ? {
            areasOfExpertise: profile.professional_context.areas_of_expertise,
            languageProficiencies: profile.professional_context.language_proficiencies.map(l => ({
              language: l.language,
              proficiency: l.level as any // Convert string to typed proficiency level
            })),
            geographicExperience: profile.professional_context.geographic_experience,
            industryExperience: profile.professional_context.industry_experience
          } : undefined,
          completionStatus: profile.professional_context?.completion_status || 'incomplete'
        });
      } catch (error) {
        console.error('Failed to fetch profile data:', error);
        // Don't show error toast - user might not have profile data yet
        setExistingProfileData(undefined);
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfileData();
  }, []);

  // Build F&O data from actual logged-in user
  const foData: FOProfileData = useMemo(() => {
    if (!user) {
      // Fallback if no user (shouldn't happen in protected route)
      return {
        employeeId: 'UNKNOWN',
        firstName: 'User',
        lastName: 'Unknown',
        email: 'unknown@eduvos.com',
        jobTitle: 'Employee',
        jobGroup: 'Not specified',
        function: 'Not specified',
        department: 'Not specified',
        functionalArea: 'Not specified',
        campus: 'Unknown',
        officeLocation: undefined,
        lineManagerId: '',
        lineManagerName: '',
        lineManagerEmail: '',
        employmentDate: new Date().toISOString().split('T')[0],
        employmentStatus: 'active',
        contractType: 'Not specified',
        performanceCycle: new Date().getFullYear().toString(),
        directReports: 0,
        isManager: false,
        reportingHierarchy: []
      };
    }

    // Determine if user is a manager from roles
    const hasManagerRole = user.roles?.includes('manager') || user.roles?.includes('hr_admin') || user.roles?.includes('admin')
    
    return {
      userId: typeof user.id === 'number' ? user.id : undefined,  // Database user ID for backend linking
      employeeId: user.employeeId || String(user.id),
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      jobTitle: user.jobTitle || 'Employee',
      jobGroup: 'General', // Not available in basic User type
      function: user.functionalArea || 'Not specified',
      department: user.department || 'Unknown',
      campus: user.campus || 'Unknown',
      officeLocation: user.officeLocation || undefined,
      lineManagerId: user.managerId || '',
      lineManagerName: manager ? `${manager.firstName} ${manager.lastName}` : '',
      lineManagerEmail: manager?.email || '',
      employmentDate: new Date().toISOString().split('T')[0],
      employmentStatus: 'active',
      contractType: user.positionType || 'Not specified',
      positionType: user.positionType || undefined,
      performanceCycle: new Date().getFullYear().toString(),
      directReports: directReportsList.length, // Use actual direct reports count
      directReportsList: directReportsList, // Pass the actual list for team confirmation
      isManager: hasManagerRole, // Derive from user roles
      faculty: user.facultyName || user.faculty || undefined,
      displayName: user.displayName || undefined,
      functionalArea: user.functionalArea || 'Not specified',
      reportingHierarchy: []
    };
  }, [user, manager, directReportsList]);

  const handleComplete = async (state: OnboardingState) => {
    if (import.meta.env.DEV) console.log('Onboarding completed:', state);
    
    try {
      // Call API to mark onboarding complete - this sets needsOnboarding to false
      const result = await completeOnboarding();
      
      if (result) {
        setOnboardingComplete(true);
        
        // Redirect after brief delay to show success message
        setTimeout(() => {
          const returnPath = (location.state as any)?.fromPath || '/employee-journey';
          if (import.meta.env.DEV) console.log('✅ Onboarding complete, navigating to:', returnPath);
          navigate(returnPath, { replace: true });
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
    }
  };

  // Loading state
  if (isLoading || loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-6"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-heritage-blue/20 rounded-full blur-xl" />
            <div className="relative bg-white/90 backdrop-blur-sm p-8 rounded-2xl border border-white/50 shadow-2xl">
              <Loader2 className="h-16 w-16 animate-spin text-heritage-blue mx-auto" />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-xl font-semibold text-heritage-blue">Checking your onboarding status...</p>
            <p className="text-sm text-muted-foreground">Please wait while we prepare your experience</p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Completion state
  if (onboardingComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-8 max-w-xl"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
            className="relative"
          >
            <div className="absolute inset-0 bg-green-500/20 rounded-full blur-3xl" />
            <div className="relative mx-auto w-28 h-28 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-2xl">
              <CheckCircle className="h-16 w-16 text-white" />
            </div>
          </motion.div>
          
          <div className="space-y-3">
            <h2 className="text-4xl font-primary font-bold text-heritage-blue">
              Welcome to Eduvos (impact & growth)+ Platform!
            </h2>
            <p className="text-lg text-muted-foreground">
              Your onboarding is complete. Redirecting you to your dashboard...
            </p>
          </div>
          
          <div className="flex items-center justify-center gap-2 text-heritage-blue">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm font-medium">Starting your journey...</span>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show wizard if user needs onboarding
  if (status) {
    return (
      <OnboardingWizard
        scenario={uiScenario}
        foData={foData}
        initialProfileData={existingProfileData}
        onComplete={handleComplete}
      />
    );
  }

  // Fallback: redirect if no onboarding needed
  return null;
}
